import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Badge,
  IconButton,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useMediaQuery,
} from '@mui/material';
import {
  Home,
  Videocam,
  AddCircle,
  DynamicFeed,
  MoreHoriz,
  Person,
  Settings,
  Groups,
  Explore,
  Notifications,
  Logout,
  Login,
  Close,
  Search,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { formatBadgeCount, getBadgeAriaLabel } from '../../utils/notificationUtils';
import { useThemeStore } from '../../stores/themeStore';
import { GRADIENT_ACCENTS } from '../../theme';
import ThemePicker from './ThemePicker';

interface NavItem {
  label: string;
  icon: React.ReactNode;
  path: string;
  isUpload?: boolean;
  isMore?: boolean;
}

/**
 * BottomNav — fixed bottom navigation bar for mobile viewports (< 768px).
 * Five items: Home, Live, Upload (elevated center), Feed, More (...).
 * "More" opens a drawer with Profile, Settings, Community, Discover, Alerts, etc.
 */
const BottomNav: React.FC = () => {
  const isMobile = useMediaQuery('(max-width:767px)');
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, logout } = useAuth();
  const { unreadCount } = useNotifications();
  const palette = useThemeStore((s) => s.palette);
  const gradient = GRADIENT_ACCENTS[palette].primary;
  const [moreOpen, setMoreOpen] = useState(false);

  if (!isMobile) return null;

  const badgeAriaLabel = getBadgeAriaLabel(unreadCount);

  const notificationsIcon = (
    <Badge
      badgeContent={formatBadgeCount(unreadCount)}
      color="error"
      invisible={!unreadCount || unreadCount === 0}
      max={99}
      aria-label={badgeAriaLabel}
    >
      <Notifications />
    </Badge>
  );

  const navItems: NavItem[] = [
    { label: 'Home', icon: <Home />, path: '/' },
    { label: 'Browse', icon: <Search />, path: '/search' },
    { label: 'Upload', icon: <AddCircle sx={{ fontSize: 36 }} />, path: '/upload', isUpload: true },
    { label: 'Feed', icon: <DynamicFeed />, path: '/feed' },
    { label: 'More', icon: <MoreHoriz />, path: '', isMore: true },
  ];

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    if (!path) return false;
    return location.pathname.startsWith(path);
  };

  const handleNavClick = (item: NavItem) => {
    if (item.isMore) {
      setMoreOpen(true);
    } else {
      navigate(item.path);
    }
  };

  const handleMoreNavigate = (path: string) => {
    setMoreOpen(false);
    navigate(path);
  };

  return (
    <>
      <Box
        component="nav"
        aria-label="Mobile navigation"
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1300,
          display: 'flex',
          justifyContent: 'space-around',
          alignItems: 'center',
          height: 64,
          backgroundColor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          pb: 'env(safe-area-inset-bottom, 0px)',
        }}
      >
        {navItems.map((item) => {
          const active = item.isMore ? moreOpen : isActive(item.path);
          return (
            <Box
              key={item.label}
              onClick={() => handleNavClick(item)}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                flex: 1,
                py: 0.5,
                ...(item.isUpload ? { mt: -2, '& .MuiSvgIcon-root': { background: gradient, borderRadius: '50%', color: '#fff', p: '2px' } } : {}),
              }}
            >
              <IconButton size="small" sx={{ color: active ? 'primary.main' : 'text.secondary', p: 0.5 }}>
                {item.icon}
              </IconButton>
              <Typography variant="caption" sx={{ fontSize: '0.6rem', fontWeight: active ? 700 : 400, color: active ? 'primary.main' : 'text.secondary', lineHeight: 1 }}>
                {item.label}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* More drawer */}
      <Drawer
        anchor="bottom"
        open={moreOpen}
        onClose={() => setMoreOpen(false)}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            maxHeight: '60vh',
            pb: 'env(safe-area-inset-bottom, 0px)',
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, pt: 1.5, pb: 0.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>More</Typography>
          <IconButton onClick={() => setMoreOpen(false)} size="small">
            <Close />
          </IconButton>
        </Box>
        <Box 
          sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
          onClick={(e: React.MouseEvent<HTMLElement>) => {
            // Find the ThemePicker IconButton inside this row and click it
            const btn = e.currentTarget.querySelector('button');
            if (btn && e.target !== btn && !(btn.contains(e.target as Node))) {
              btn.click();
            }
          }}
        >
          <ThemePicker onSelect={() => setMoreOpen(false)} />
          <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary', pointerEvents: 'none' }}>Theme</Typography>
        </Box>
        <Divider />
        <List>
          {isAuthenticated && (
            <ListItem onClick={() => handleMoreNavigate(`/profile/${user?.id || 'me'}`)} sx={{ cursor: 'pointer' }}>
              <ListItemIcon><Person /></ListItemIcon>
              <ListItemText primary="Profile" />
            </ListItem>
          )}
          <ListItem onClick={() => handleMoreNavigate('/discover')} sx={{ cursor: 'pointer' }}>
            <ListItemIcon><Explore /></ListItemIcon>
            <ListItemText primary="Community" />
          </ListItem>
          <ListItem onClick={() => handleMoreNavigate('/live')} sx={{ cursor: 'pointer' }}>
            <ListItemIcon><Videocam /></ListItemIcon>
            <ListItemText primary="Live" />
          </ListItem>
          <ListItem onClick={() => handleMoreNavigate('/notifications')} sx={{ cursor: 'pointer' }}>
            <ListItemIcon>
              <Badge
                badgeContent={formatBadgeCount(unreadCount)}
                color="error"
                invisible={!unreadCount || unreadCount === 0}
                max={99}
                aria-label={badgeAriaLabel}
              >
                <Notifications />
              </Badge>
            </ListItemIcon>
            <ListItemText primary="Notifications" />
          </ListItem>
          {isAuthenticated && (
            <ListItem onClick={() => handleMoreNavigate('/settings')} sx={{ cursor: 'pointer' }}>
              <ListItemIcon><Settings /></ListItemIcon>
              <ListItemText primary="Settings" />
            </ListItem>
          )}
          <Divider />
          {isAuthenticated ? (
            <ListItem onClick={() => { setMoreOpen(false); logout(); navigate('/'); }} sx={{ cursor: 'pointer' }}>
              <ListItemIcon><Logout /></ListItemIcon>
              <ListItemText primary="Logout" />
            </ListItem>
          ) : (
            <ListItem onClick={() => handleMoreNavigate('/login')} sx={{ cursor: 'pointer' }}>
              <ListItemIcon><Login /></ListItemIcon>
              <ListItemText primary="Login" />
            </ListItem>
          )}
        </List>
      </Drawer>
    </>
  );
};

export default BottomNav;
